"use client";

import {
  type Control,
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { Input } from "@/components/ui";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
} from "@/components/ui/multi-select";
import MultiTagInput from "@/components/ui/multi-tag";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getOptionLabel } from "@/lib/measurement-calculations";
import { getStepForField } from "@/lib/measurement-utils";

import type { FieldOption, MeasurementField } from "@/types/measurement";

interface MeasurementInputProps<TForm extends FieldValues> {
  field: Pick<
    MeasurementField,
    "id" | "dataType" | "decimalPlaces" | "minValue" | "maxValue" | "options"
  >;
  control: Control<TForm>;
  name: FieldPath<TForm>;
  rules?: ControllerProps<TForm>["rules"];
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  inputId?: string;
  placeholder?: string;
}

/**
 * Renders the right input for a measurement field, branching on data type
 * and whether the field has a bounded options list. See the matrix in
 * specs/multi-value-measurements-frontend.md.
 */
export function MeasurementInput<TForm extends FieldValues>({
  field,
  control,
  name,
  rules,
  disabled,
  hasError,
  errorMessage,
  inputId,
  placeholder,
}: MeasurementInputProps<TForm>) {
  const hasOptions = !!field.options && field.options.length > 0;
  const options = (field.options ?? []) as FieldOption[];

  if (field.dataType === "STRING_ARRAY") {
    if (hasOptions) {
      return (
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field: rhfField }) => {
            const value = normaliseArrayValue(rhfField.value);
            return (
              <div data-testid={`field-input-multi-select-${field.id}`}>
                <MultiSelect
                  options={options.map((o) => ({
                    value: String(o.value),
                    label: o.label?.trim() || String(o.value),
                  }))}
                  value={value}
                  onChange={rhfField.onChange}
                  hasError={hasError}
                  errorMessage={errorMessage}
                  disabled={disabled}
                >
                  <MultiSelectTrigger
                    placeholder={placeholder ?? "Select..."}
                  />
                  <MultiSelectContent>
                    {options.map((o) => (
                      <MultiSelectItem
                        key={String(o.value)}
                        value={String(o.value)}
                      >
                        {o.label?.trim() || String(o.value)}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectContent>
                </MultiSelect>
              </div>
            );
          }}
        />
      );
    }
    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: rhfField }) => {
          const tags = normaliseArrayValue(rhfField.value);
          return (
            <div data-testid={`field-input-array-${field.id}`}>
              <MultiTagInput
                tags={tags}
                onAddTag={(tag) => {
                  if (tags.includes(tag)) return;
                  rhfField.onChange([...tags, tag]);
                }}
                onRemoveTag={(idx) => {
                  rhfField.onChange(tags.filter((_, i) => i !== idx));
                }}
                placeholder={placeholder ?? "Type a value and press Enter"}
                hasError={hasError}
                errorMessage={errorMessage}
              />
            </div>
          );
        }}
      />
    );
  }

  if (hasOptions) {
    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: rhfField }) => (
          <div data-testid={`field-input-options-select-${field.id}`}>
            <Select
              value={rhfField.value == null ? "" : String(rhfField.value)}
              onValueChange={rhfField.onChange}
              disabled={disabled}
            >
              <SelectTrigger
                id={inputId}
                hasError={hasError}
                errorMessage={errorMessage}
              >
                <SelectValue placeholder={placeholder ?? "Select..."} />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={String(o.value)} value={String(o.value)}>
                    {getOptionLabel({ options }, o.value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      />
    );
  }

  const isNumeric = field.dataType === "FLOAT" || field.dataType === "INTEGER";
  const isDate = field.dataType === "DATE";

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: rhfField }) => (
        <Input
          id={inputId}
          data-testid={`field-input-${field.id}`}
          type={isNumeric ? "number" : isDate ? "date" : "text"}
          step={
            isNumeric
              ? getStepForField(field.dataType, field.decimalPlaces ?? null)
              : undefined
          }
          min={isNumeric && field.minValue != null ? field.minValue : undefined}
          max={isNumeric && field.maxValue != null ? field.maxValue : undefined}
          placeholder={placeholder}
          value={rhfField.value == null ? "" : String(rhfField.value)}
          onChange={(e) => rhfField.onChange(e.target.value)}
          onBlur={rhfField.onBlur}
          disabled={disabled}
          hasError={hasError}
          errorMessage={errorMessage}
        />
      )}
    />
  );
}

function normaliseArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  return [];
}
